import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AuthLayout, FormError, FormNotice, authButton, authInput } from "@/components/auth/AuthLayout";
import { safeNext } from "@/components/auth/RouteGuards";
import { api } from "@/lib/api";
import { authErrorMessage } from "@/lib/authErrors";
import { supabase } from "@/lib/supabase";

export default function SignIn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [unconfirmed, setUnconfirmed] = useState(false);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setUnconfirmed(false);
    setBusy(true);
    const { error: err } = await supabase.auth.signInWithPassword({ email: email.trim(), password });
    setBusy(false);
    if (err) {
      setUnconfirmed(err.code === "email_not_confirmed");
      setError(authErrorMessage(err));
      return;
    }
    navigate(safeNext(params.get("next")), { replace: true });
  };

  const resend = async () => {
    try {
      await api.post("/auth/resend-verification", { email: email.trim() }, { auth: false });
      setError("");
      setNotice("If your account still needs verifying, a new link is on its way. Check your inbox.");
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <AuthLayout
      testId="sign-in-page"
      title="Welcome back"
      subtitle="Sign in to continue to ChatSleek."
      footer={
        <>
          New to ChatSleek?{" "}
          <Link to={`/sign-up${params.get("next") ? `?next=${encodeURIComponent(params.get("next"))}` : ""}`} className="text-white underline-offset-4 hover:underline" data-testid="login-register-link">
            Create an account
          </Link>
        </>
      }
    >
      <form onSubmit={submit} className="space-y-3.5">
        <input type="email" required autoComplete="email" placeholder="Email" value={email} onChange={(e) => setEmail(e.target.value)} className={authInput} data-testid="login-email-input" />
        <input type="password" required autoComplete="current-password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} className={authInput} data-testid="login-password-input" />
        <div className="flex justify-end">
          <Link to="/forgot-password" className="text-sm text-zinc-400 hover:text-white" data-testid="login-forgot-password-link">
            Forgot password?
          </Link>
        </div>
        <FormError>{error}</FormError>
        {unconfirmed && (
          <button type="button" onClick={resend} className="w-full rounded-xl border border-white/12 py-2.5 text-sm text-zinc-200 hover:bg-white/[0.05]" data-testid="login-resend-verification-button">
            Resend verification email
          </button>
        )}
        <FormNotice>{notice}</FormNotice>
        <button type="submit" disabled={busy} className={authButton} data-testid="login-submit-button">
          {busy ? "Signing in…" : "Sign in"}
        </button>
      </form>
    </AuthLayout>
  );
}
